import React from "react";
import { observer } from "mobx-react";
import ReactApexChart from "react-apexcharts";
import { baseChartOptions, axisLabelStyle, divergingColors } from "../visuals/chartTheme";
import { formatMetric } from "../visuals/formatMetric";
import { useTokenHost } from "../visuals/cssVar";

/**
 * Values either side of a zero baseline — change vs. previous period, variance
 * to target. The midpoint is neutral grey: putting a hue there invents a
 * category the data does not contain.
 */
const DivergingBarBlock = observer(({ block }) => {
  // `ready` forces one re-render after mount so token reads see a live ref.
  const [rootRef, ready] = useTokenHost();
  const rows = (block.rows || []).filter(
    row => row && row.value != null && Number.isFinite(Number(row.value))
  );
  if (!rows.length) return null;

  const colors = divergingColors(rootRef.current);
  const unit = block.unit || "";
  const values = rows.map(row => Number(row.value));

  const options = {
    ...baseChartOptions(rootRef.current),
    chart: { ...baseChartOptions(rootRef.current).chart, type: "bar" },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: "62%", distributed: true }
    },
    colors: values.map(value =>
      value > 0 ? colors.positive : value < 0 ? colors.negative : colors.mid
    ),
    xaxis: {
      categories: rows.map(row => row.label),
      labels: { ...axisLabelStyle(rootRef.current), formatter: value => formatMetric(value, { unit }) },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: axisLabelStyle(rootRef.current) },
    tooltip: {
      y: { formatter: value => formatMetric(value, { unit }) }
    }
  };

  return (
    <div className="cpVisual" ref={rootRef}>
      {block.title && <div className="cpVisual__title">{block.title}</div>}
      <ReactApexChart
        key={ready ? "themed" : "initial"}
        options={options}
        series={[{ name: block.seriesName || "Change", data: values }]}
        type="bar"
        height={Math.max(140, rows.length * 34)}
      />
    </div>
  );
});

export default DivergingBarBlock;
